import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import AppHeader from "@/components/AppHeader";
import FeatureCard from "@/components/FeatureCard";
import { useSEO } from "@/lib/seo";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Play, PlusCircle, KeyRound, ArrowRight, Trophy, Users, Sparkles } from "lucide-react";
import { requestPushPermission } from "@/lib/push";
import { toast } from "sonner";

export default function HomePage() {
  useSEO({ title: "Ana Sayfa", description: "Aktif masaların, arkadaşların ve son maç sonuçların tek yerde." });
  const { user } = useAuth();
  const nav = useNavigate();
  const [active, setActive] = useState(null);
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [pushAsked, setPushAsked] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [a, s, m] = await Promise.all([
          api.get("/physical/matches/active"),
          api.get("/physical/statistics/me"),
          api.get("/physical/matches?limit=3"),
        ]);
        setActive(a.data.match);
        setStats(s.data);
        setRecent(m.data.matches || []);
      } catch (_) {}
    })();
  }, []);

  const askPush = async () => {
    setPushAsked(true);
    const r = await requestPushPermission();
    if (r.ok) toast.success("Bildirimler açıldı!");
    else if (r.reason === "denied") toast.error("Bildirim izni reddedildi");
    else if (r.reason === "unsupported") toast.error("Tarayıcınız bildirim desteklemiyor");
  };

  return (
    <AppShell>
      <AppHeader title="Ana Sayfa" showLogo />
      <div className="px-4 pt-4 space-y-5">
        {/* Welcome + Hero */}
        <div>
          <p className="text-sm text-muted-foreground">Hoş geldiniz,</p>
          <h2 className="font-display font-extrabold text-3xl tracking-tight">
            {user?.display_name?.split(" ")[0] || user?.username || "Oyuncu"} 👋
          </h2>
          <p className="text-muted-foreground text-sm mt-1">
            İster aynı masada, ister kilometrelerce uzakta.
          </p>
        </div>

        {/* Continue interrupted */}
        {active && (
          <Card
            data-testid="continue-card"
            className="rounded-2xl border-primary/30 bg-primary/5 p-4 flex items-center gap-3 cursor-pointer hover:bg-primary/10 transition-colors"
            onClick={() => nav(`/fiziksel/${active.id}`)}
          >
            <div className="h-12 w-12 rounded-full bg-primary/15 flex items-center justify-center text-primary">
              <Play className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-primary uppercase tracking-wider">Devam Eden Masa</p>
              <p className="font-semibold truncate">{active.title}</p>
              <p className="text-xs text-muted-foreground">
                {active.rounds?.length || 0} el oynandı · {active.players?.length} oyuncu
              </p>
            </div>
            <ArrowRight className="h-5 w-5 text-primary" />
          </Card>
        )}

        {/* Quick actions — her kart kendi tonuyla ayrışır */}
        <div className="space-y-3">
          <FeatureCard
            tone="blue"
            testId="home-online-room"
            onClick={() => nav("/online/yeni")}
            icon={<PlusCircle className="h-5 w-5" />}
            title="Online Masa Kur"
            subtitle="Arkadaşlarınla gerçek zamanlı Okey"
          />

          <FeatureCard
            tone="green"
            testId="home-physical-start"
            onClick={() => nav("/fiziksel/yeni")}
            icon={<Play className="h-5 w-5" />}
            title="Fiziksel Oyun Başlat"
            subtitle="Masa başındaysanız skorları takip edin"
            filled
          />

          <FeatureCard
            tone="yellow"
            testId="home-room-code"
            onClick={() => nav("/online/katil")}
            icon={<KeyRound className="h-5 w-5" />}
            title="Oda Koduyla Katıl"
            subtitle="6 haneli kod ile katılın"
          />
        </div>

        {/* Push permission */}
        {typeof Notification !== "undefined" && Notification.permission === "default" && !pushAsked && (
          <Card className="rounded-2xl p-4 border-accent/50 bg-accent/10">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-full bg-accent/30 flex items-center justify-center text-accent-foreground">
                <Sparkles className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold">Bildirimleri aç</p>
                <p className="text-xs text-muted-foreground mb-3">
                  Sıranız geldiğinde ve arkadaşlarınız sizi masaya çağırdığında haberdar olun.
                </p>
                <Button
                  data-testid="enable-push"
                  onClick={askPush}
                  size="sm"
                  className="rounded-full h-9"
                >
                  Bildirimleri Aç
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Stats summary */}
        {stats && (
          <div className="grid grid-cols-3 gap-3">
            <StatCard label="Maç" value={stats.total_matches} testId="stat-matches" />
            <StatCard label="Galibiyet" value={stats.wins} testId="stat-wins" />
            <StatCard label="Kazanma %" value={`${stats.win_rate}`} testId="stat-winrate" />
          </div>
        )}
        {stats && stats.online_total > 0 && (
          <div className="rounded-2xl border border-primary/20 bg-primary/5 p-3 flex items-center justify-between text-xs">
            <span className="text-muted-foreground">
              <span className="font-semibold text-primary">Online:</span> {stats.online_wins}/{stats.online_total} kazandı
            </span>
            <span className="text-muted-foreground">
              <span className="font-semibold text-primary">Fiziksel:</span> {stats.physical_wins}/{stats.physical_total} kazandı
            </span>
          </div>
        )}

        {/* Recent */}
        {recent.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold text-lg">Son Maçlar</h3>
              <button
                onClick={() => nav("/mac-gecmisi")}
                className="text-xs text-primary font-semibold"
                data-testid="home-view-history"
              >
                Tümü →
              </button>
            </div>
            <div className="space-y-2">
              {recent.map((m) => (
                <div
                  key={m.id}
                  className="rounded-xl border border-border bg-card p-3 flex items-center gap-3 cursor-pointer hover:bg-secondary/40 transition-colors"
                  onClick={() => nav(`/fiziksel/${m.id}`)}
                  data-testid={`recent-match-${m.id}`}
                >
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Trophy className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{m.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {m.players?.length} oyuncu · {m.rounds?.length || 0} el · {m.status === "completed" ? "Tamamlandı" : m.status === "active" ? "Devam ediyor" : "İptal"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Friend group hint */}
        <Card className="rounded-2xl p-4 flex items-center gap-3 cursor-pointer hover:bg-secondary/40 transition-colors" onClick={() => nav("/arkadaslar")}>
          <div className="h-10 w-10 rounded-full bg-accent/20 flex items-center justify-center">
            <Users className="h-5 w-5 text-accent-foreground" />
          </div>
          <div className="flex-1">
            <p className="font-semibold">Arkadaş grubunu kur</p>
            <p className="text-xs text-muted-foreground">Grup lider tablosu ve toplu davet için</p>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
        </Card>
      </div>
    </AppShell>
  );
}

function StatCard({ label, value, testId }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-3 text-center" data-testid={testId}>
      <div className="font-display font-extrabold text-2xl text-primary tabular-nums">{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
    </div>
  );
}
